import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import {
  AuditRecord,
  AuditFilters,
  AuditStats,
  TimeSignature,
  MonthlyTimeSummary,
  AuditExport,
  AuditAction,
} from '@/types/timeTracking';

// =====================================================
// QUERIES
// =====================================================

export function useAuditLogs(filters: AuditFilters = {}) {
  return useQuery({
    queryKey: ['audit_logs', filters],
    queryFn: async (): Promise<AuditRecord[]> => {
      let query = api
        .from('auditoria_fichajes')
        .select(`
          *,
          user:user_id (id, full_name, employee_id),
          performer:performed_by (id, full_name)
        `)
        .order('performed_at', { ascending: false })
        .limit(500);

      if (filters.userId) {
        query = query.eq('user_id', filters.userId);
      }
      if (filters.action) {
        query = query.eq('action', filters.action);
      }
      if (filters.dateFrom) {
        query = query.gte('performed_at', `${filters.dateFrom}T00:00:00`);
      }
      if (filters.dateTo) {
        query = query.lte('performed_at', `${filters.dateTo}T23:59:59`);
      }
      if (filters.performedBy) {
        query = query.eq('performed_by', filters.performedBy);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
  });
}

export function useAuditStats(dateFrom?: string, dateTo?: string) {
  const defaultFrom = format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd');
  const defaultTo = format(new Date(), 'yyyy-MM-dd');

  return useQuery({
    queryKey: ['audit_stats', dateFrom || defaultFrom, dateTo || defaultTo],
    queryFn: async (): Promise<AuditStats> => {
      const from = dateFrom || defaultFrom;
      const to = dateTo || defaultTo;

      const { data: logs, error: logsError } = await api
        .from('auditoria_fichajes')
        .select('action, user_id')
        .gte('performed_at', `${from}T00:00:00`)
        .lte('performed_at', `${to}T23:59:59`);

      if (logsError) throw logsError;

      const { data: signatures, error: sigError } = await api
        .from('time_entry_signatures')
        .select('status');

      if (sigError) throw sigError;

      const { data: lastExport, error: expError } = await api
        .from('audit_exports')
        .select(`*, requester:requested_by (id, full_name)`)
        .order('requested_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (expError) throw expError;

      const byAction: Record<string, number> = {};
      const byUserMap = new Map<string, number>();

      for (const log of logs || []) {
        byAction[log.action] = (byAction[log.action] || 0) + 1;
        byUserMap.set(log.user_id, (byUserMap.get(log.user_id) || 0) + 1);
      }

      const { data: profiles } = await api
        .from('profiles')
        .select('id, full_name')
        .in('id', Array.from(byUserMap.keys()));

      const profileMap = new Map((profiles || []).map(p => [p.id, p.full_name]));

      const byUser = Array.from(byUserMap.entries())
        .map(([user_id, count]) => ({
          user_id,
          full_name: profileMap.get(user_id) || 'Desconocido',
          count,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      return {
        total_records: logs?.length || 0,
        by_action: byAction as Record<AuditAction, number>,
        by_user: byUser,
        corrections_count: byAction['correction'] || 0,
        signatures_pending: signatures?.filter(s => s.status === 'pending').length || 0,
        signatures_completed: signatures?.filter(s => s.status === 'signed').length || 0,
        last_export: lastExport,
      };
    },
  });
}

export function useTimeSignatures(userId?: string, status?: string) {
  return useQuery({
    queryKey: ['time_signatures', userId, status],
    queryFn: async (): Promise<TimeSignature[]> => {
      let query = api
        .from('time_entry_signatures')
        .select(`
          *,
          user:user_id (id, full_name, employee_id)
        `)
        .order('period_start', { ascending: false });

      if (userId) {
        query = query.eq('user_id', userId);
      }
      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
  });
}

export function useMyPendingSignatures() {
  const { userId } = useAuth();

  return useQuery({
    queryKey: ['my_pending_signatures', userId],
    queryFn: async (): Promise<MonthlyTimeSummary[]> => {
      if (!userId) return [];

      const { data: summaries, error: sumError } = await api
        .from('monthly_time_summary')
        .select('*')
        .eq('user_id', userId)
        .order('month_start', { ascending: false })
        .limit(12);

      if (sumError) throw sumError;

      const { data: signatures, error: sigError } = await api
        .from('time_entry_signatures')
        .select('*')
        .eq('user_id', userId);

      if (sigError) throw sigError;

      const signedPeriods = new Set(
        (signatures || []).map(s => `${s.period_start}_${s.period_end}`)
      );

      return (summaries || []).map(s => ({
        ...s,
        signature: (signatures || []).find(
          sig => sig.period_start === s.month_start && sig.period_end === s.month_end
        ) || null,
      })).filter(s => !signedPeriods.has(`${s.month_start}_${s.month_end}`) || s.signature?.status === 'pending');
    },
    enabled: !!userId,
  });
}

export function useMonthlyTimeSummaries(userId?: string) {
  return useQuery({
    queryKey: ['monthly_time_summaries', userId],
    queryFn: async (): Promise<MonthlyTimeSummary[]> => {
      let query = api
        .from('monthly_time_summary')
        .select('*')
        .order('month_start', { ascending: false })
        .limit(24);

      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
  });
}

export function useAuditExports() {
  return useQuery({
    queryKey: ['audit_exports'],
    queryFn: async (): Promise<AuditExport[]> => {
      const { data, error } = await api
        .from('audit_exports')
        .select(`
          *,
          requester:requested_by (id, full_name)
        `)
        .order('requested_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data || [];
    },
  });
}

// =====================================================
// MUTATIONS
// =====================================================

export function useSignTimePeriod() {
  const queryClient = useQueryClient();
  const { userId } = useAuth();

  return useMutation({
    mutationFn: async ({
      periodStart,
      periodEnd,
      signatureData,
      employeeNotes,
    }: {
      periodStart: string;
      periodEnd: string;
      signatureData?: string;
      employeeNotes?: string;
    }) => {
      const { data, error } = await api.rpc('sign_time_period', {
        p_user_id: userId,
        p_period_start: periodStart,
        p_period_end: periodEnd,
        p_signature_data: signatureData || null,
        p_employee_notes: employeeNotes || null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time_signatures'] });
      queryClient.invalidateQueries({ queryKey: ['my_pending_signatures'] });
      queryClient.invalidateQueries({ queryKey: ['audit_logs'] });
      queryClient.invalidateQueries({ queryKey: ['audit_stats'] });
    },
  });
}

export function useLogAuditAction() {
  const queryClient = useQueryClient();
  const { userId } = useAuth();

  return useMutation({
    mutationFn: async ({
      eventId,
      targetUserId,
      action,
      oldData,
      newData,
      reason,
    }: {
      eventId?: string;
      targetUserId: string;
      action: AuditAction;
      oldData?: Record<string, any>;
      newData?: Record<string, any>;
      reason?: string;
    }) => {
      const { data, error } = await api.rpc('log_time_audit', {
        p_event_id: eventId || null,
        p_user_id: targetUserId,
        p_action: action,
        p_old_data: oldData || null,
        p_new_data: newData || null,
        p_reason: reason || null,
        p_performed_by: userId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audit_logs'] });
      queryClient.invalidateQueries({ queryKey: ['audit_stats'] });
    },
  });
}

export function useRecordExport() {
  const queryClient = useQueryClient();
  const { userId } = useAuth();

  return useMutation({
    mutationFn: async ({
      exportType,
      format,
      userIds,
      dateFrom,
      dateTo,
      fileName,
      recordCount,
      purpose,
    }: {
      exportType: 'time_entries' | 'daily_summary' | 'monthly_report' | 'full_audit';
      format: 'xlsx' | 'pdf' | 'csv' | 'json';
      userIds?: string[];
      dateFrom: string;
      dateTo: string;
      fileName?: string;
      recordCount?: number;
      purpose?: 'internal_audit' | 'labor_inspection' | 'employee_request';
    }) => {
      const { data, error } = await api
        .from('audit_exports')
        .insert({
          export_type: exportType,
          format,
          user_ids: userIds || null,
          date_from: dateFrom,
          date_to: dateTo,
          file_name: fileName || null,
          record_count: recordCount || null,
          purpose: purpose || null,
          requested_by: userId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audit_exports'] });
      queryClient.invalidateQueries({ queryKey: ['audit_stats'] });
    },
  });
}

export function useDisputeSignature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      signatureId,
      notes,
    }: {
      signatureId: string;
      notes: string;
    }) => {
      const { error } = await api
        .from('time_entry_signatures')
        .update({
          status: 'disputed',
          employee_notes: notes,
        })
        .eq('id', signatureId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time_signatures'] });
      queryClient.invalidateQueries({ queryKey: ['my_pending_signatures'] });
    },
  });
}
